/**
 * @file api.ts
 * @brief Queries for "Use my data to improve models" (ADR-048 Part 2).
 *
 * @details
 *   useModelTraining          → GET /api/auth/model-training
 *   useSaveModelTraining      → PUT /api/auth/model-training { optIn, version }
 *   useCompanyModelTraining   → GET /api/company/model-training (company admin)
 *   useSaveCompanyModelTraining → PUT /api/company/model-training { allowMembers }
 *
 * Both saves are optimistic and put the old value back on error. The client
 * interceptor already sends `x-client-surface: web|desktop`, which the backend
 * stores on the consent record. A company change also refreshes the admin's
 * own card (their `companyManaged` follows the switch).
 *
 * @references
 * - BACKEND/src/features/auth/routes.js, src/features/company/routes.js
 * - scratch/claude-code-report.md ("Part 2: client contract")
 *
 * @author DES (prepared with Claude)
 * @date 20-10-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet, apiPut } from "@/shared/api/client";
import { authEndpoints, companyEndpoints } from "@/shared/api/endpoints";
import type { CompanyModelTrainingState, ModelTrainingState } from "./modelTraining";

const WordingSchema = z.object({
  version: z.string(),
  title: z.string(),
  body: z.string(),
  privacyUrl: z.string(),
  managedByCompanyText: z.string(),
});

const StateSchema = z.object({
  optIn: z.boolean(),
  effective: z.boolean(),
  companyManaged: z.boolean(),
  wording: WordingSchema,
});

const CompanyStateSchema = z.object({
  allowMembers: z.boolean(),
  wording: z.object({ title: z.string(), body: z.string(), privacyUrl: z.string() }),
});

export const modelTrainingKey = ["auth", "model-training"] as const;
export const companyModelTrainingKey = ["company", "model-training"] as const;

export function useModelTraining(enabled = true) {
  return useQuery<ModelTrainingState>({
    queryKey: modelTrainingKey,
    enabled,
    queryFn: async () => StateSchema.parse(await apiGet<unknown>(authEndpoints.modelTraining)),
  });
}

export function useSaveModelTraining() {
  const qc = useQueryClient();
  return useMutation<ModelTrainingState, Error, { optIn: boolean; version: string }, { prev?: ModelTrainingState }>({
    mutationFn: async (body) => StateSchema.parse(await apiPut<unknown>(authEndpoints.modelTraining, body)),
    onMutate: async ({ optIn }) => {
      await qc.cancelQueries({ queryKey: modelTrainingKey });
      const prev = qc.getQueryData<ModelTrainingState>(modelTrainingKey);
      if (prev) qc.setQueryData<ModelTrainingState>(modelTrainingKey, { ...prev, optIn, effective: optIn && !prev.companyManaged });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(modelTrainingKey, ctx.prev);
    },
    onSuccess: (data) => qc.setQueryData(modelTrainingKey, data),
  });
}

export function useCompanyModelTraining(enabled = true) {
  return useQuery<CompanyModelTrainingState>({
    queryKey: companyModelTrainingKey,
    enabled,
    queryFn: async () => CompanyStateSchema.parse(await apiGet<unknown>(companyEndpoints.modelTraining)),
  });
}

export function useSaveCompanyModelTraining() {
  const qc = useQueryClient();
  return useMutation<CompanyModelTrainingState, Error, { allowMembers: boolean }, { prev?: CompanyModelTrainingState }>({
    mutationFn: async (body) => CompanyStateSchema.parse(await apiPut<unknown>(companyEndpoints.modelTraining, body)),
    onMutate: async ({ allowMembers }) => {
      await qc.cancelQueries({ queryKey: companyModelTrainingKey });
      const prev = qc.getQueryData<CompanyModelTrainingState>(companyModelTrainingKey);
      if (prev) qc.setQueryData<CompanyModelTrainingState>(companyModelTrainingKey, { ...prev, allowMembers });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(companyModelTrainingKey, ctx.prev);
    },
    onSuccess: (data) => {
      qc.setQueryData(companyModelTrainingKey, data);
      void qc.invalidateQueries({ queryKey: modelTrainingKey });
    },
  });
}
